/**
 * History Manager Service for Lobby2.0
 * Replicated from the old lobby's HistoryManagerService
 * Handles game navigation and browser history management
 */

import type { IStartGameParams } from 'types/store/IStartGameStore';
import { externalCommunication } from '../communication/ExternalCommunication';
import type { IHistoryState } from 'types/window.types';

export interface IHistoryManagerSettings {
  closeUrl: string;
  isAggregator: boolean;
  gameKey: string;
}

export default class HistoryManagerService {
  private settings: IHistoryManagerSettings;
  private exitingViaBrowserBackButton: boolean = false;
  private startingNewGameInProgress: boolean = false;
  private redirects = 0;
  private boundOnPopState: (event: PopStateEvent) => void;

  constructor(settings: IHistoryManagerSettings) {
    this.settings = settings;
    this.boundOnPopState = this.onPopState.bind(this);
    this.initBrowserHistory();
    this.attachListeners();
  }

  /**
   * Handle game start - replicated from old lobby
   */
  public onStartNewGame(params: IStartGameParams) {
    this.startingNewGameInProgress = true;
    const newUrl = this.parseNewGameURL(params);

    setTimeout(() => {
      // Keep backward compatibility
      if (!this.settings.isAggregator) {
        const newState: Partial<IHistoryState> = { ...window.history.state };
        if (newState?.redirects) {
          newState.redirects = newState.redirects + 1;
          try {
            window.history.pushState(newState, '', newUrl.href);
          } catch (pushStateError) {
            // Silent fail
          }
        }
      }

      document.location.assign(newUrl.href);
    }, 400); // delay for sound effects
  }

  /**
   * Handle exit application
   */
  public onExitApplication(): void {
    if (!this.exitingViaBrowserBackButton) {
      externalCommunication.sendExit();
    }

    setTimeout(() => {
      if (this.settings.closeUrl && !this.exitingViaBrowserBackButton) {
        window.location.href = this.settings.closeUrl;
      } else {
        if (history.length) {
          const backN = this.calculateBackSteps();
          const safeBackN = Math.min(backN, history.length - 1);
          if (safeBackN > 0) {
            history.go(-1 * safeBackN);
          }
        }

        try {
          window.close();
        } catch (e) {
          // Exception during window.close()
        }
      }
    }, 100);
  }

  /**
   * Initialize browser history state
   */
  private initBrowserHistory(): void {
    if (!history?.state?.redirects) {
      const urlParams = new URLSearchParams(window.location.search);
      const cfl = urlParams.has('comeFromLobby') ? parseInt(urlParams.get('comeFromLobby') || '0', 10) : 0;

      const step = this.settings.isAggregator ? 1 : 2;
      const redirects = !this.settings.isAggregator ? step : cfl ? cfl + step : step;
      
      const newState: IHistoryState = {
        redirects: redirects,
      };

      try {
        history.pushState(newState, '', window.location.href);
        this.redirects = newState.redirects;
      } catch (pushStateError) {
        // Silent fail
      }
    } else {
      this.redirects = history?.state?.redirects;
    }
  }

  /**
   * Handle popstate events (browser back button)
   */
  private onPopState(_event: PopStateEvent): void {
    if (this.startingNewGameInProgress) {
      return;
    }

    this.exitingViaBrowserBackButton = true;
    this.onExitApplication();
  }

  /**
   * Parse and construct new game URL
   */
  private parseNewGameURL(params: IStartGameParams): URL {
    const newUrl = new URL(document.location.href);
    const urlParams = new URLSearchParams(window.location.search);
    let cfl = urlParams.has('comeFromLobby') ? parseInt(urlParams.get('comeFromLobby') || '0', 10) : 0;
    cfl++;

    // parseNewGameURL initial state processed

    urlParams.set('comeFromLobby', String(cfl));
    urlParams.set('sessionToken', params.defenceCode);
    urlParams.set('gameKey', params.gameKey);
    urlParams.set('language', params.language.toLowerCase());
    urlParams.set('bgMusicMute', String(params.bgMusicMute));
    urlParams.set('mute', String(params.mute));
    // Remove openSection parameter if it exists
    urlParams.delete('openSection');
    newUrl.search = urlParams.toString();

    const redirectHost = params.redirectHost;
    // Processing redirectHost parameter
    
    if (redirectHost && redirectHost.length) {
      const parsedRedirectHost = new URL(redirectHost);
      // Parsed redirectHost successfully
      
      newUrl.host = redirectHost.indexOf('://') !== -1 ? redirectHost.split('://')[1] : redirectHost;
      newUrl.pathname = parsedRedirectHost.pathname;
      newUrl.href = newUrl.href.replace('/9000', ''); // for local dev only
      
      // Updated URL with redirectHost
    } else {
      // No redirectHost provided, using current pathname logic
      newUrl.href = newUrl.href.replace(newUrl.pathname, '');
    }

    // Final URL constructed
    return newUrl;
  }

  /**
   * Calculate how many steps to go back in history
   */
  private calculateBackSteps(): number {
    if (!this.settings.isAggregator) {
      if (this.exitingViaBrowserBackButton) {
        return this.redirects - 1;
      }
      return history?.state?.redirects || 0;
    }
    return !this.exitingViaBrowserBackButton ? this.redirects + 1 : this.redirects;
  }

  /**
   * Attach event listeners
   */
  private attachListeners(): void {
    window.addEventListener('popstate', this.boundOnPopState, false);
  }

  /**
   * Cleanup
   */
  public cleanup(): void {
    window.removeEventListener('popstate', this.boundOnPopState, false);
  }
}
